/*
  
author: Christophjer C. Alves Martins
This component is used to display the status of a machine and allow the user to trigger actions on it.

*/

import DesktopMacOutlinedIcon from "@mui/icons-material/DesktopMacOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { AutomationClientActionsType } from "../../../api/compute/Automation";

type MachineProps = {
  name?: string;
  isServing?: boolean | null;
  mode?: string | null;
  lb?: string | null;
  sync?: boolean | null;
  process_status: string;
  timestamp: number;
  update?: boolean | null;
  notifications?: string | null;
  triggerAction: (actionType: AutomationClientActionsType) => void;
  logMasterHandler?: () => void;
  LED?: number;
};

export const stateColors: { [key: number]: string } = {
  "-1": "red",
  0: "#f5e79e",
  1: "green",
  "-2": "gray",
};

export default function Machine({
  name = "Master",
  isServing,
  mode,
  lb,
  process_status,
  timestamp,
  update = null,
  notifications = null,
  sync = null,
  triggerAction,
  logMasterHandler,
  LED = -2,
}: MachineProps) {
  const [showDetails, setShowDetails] = useState(false);

  const [processingAction, setProcessingAction] = useState<boolean>(false);
  const [actionTriggered, setActionTriggered] =
    useState<AutomationClientActionsType | null>(null);
  const handleTriggerAction = useCallback(
    async (action: AutomationClientActionsType) => {
      setProcessingAction(true);
      setActionTriggered(action);
      await triggerAction(action);
      setActionTriggered(null);
      setProcessingAction(false);
    },
    [triggerAction],
  );

  const [logMaster, setLogMaster] = useState<any>(null);
  const [showLogMaster, setShowLogMaster] = useState(false);
  const [fetchingLogMaster, setFetchingLogMaster] = useState<boolean>(false);
  const getLogMaster = useCallback(async () => {
    if (logMasterHandler) {
      setFetchingLogMaster(true);
      const _logMaster = await logMasterHandler();
      setLogMaster(_logMaster);
      setShowLogMaster(true);
      setFetchingLogMaster(false);
    }
  }, [logMasterHandler]);
  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      boxShadow={showDetails ? 5 : 1}
      border={
        showDetails
          ? "1px solid " + stateColors[LED]
          : "1px solid " + stateColors[LED]
      }
      borderLeft={"5px solid" + stateColors[LED]}
      p={1}
      borderRadius={1}
    >
      <Box display={"flex"} gap={1} alignItems={"center"}>
        <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
          <DesktopMacOutlinedIcon
            sx={{
              color: stateColors[LED],
            }}
          />
          <Typography>
            <strong>{name}</strong>
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        {isServing != null && (
          <>
            <Typography>Serving: {isServing ? "Yes" : "No"}</Typography>
            {!isServing ? (
              <Button
                color="secondary"
                disabled={processingAction}
                onClick={() => handleTriggerAction("SET_ONLINE")}
                startIcon={
                  actionTriggered === "SET_ONLINE" && processingAction ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Set Online
              </Button>
            ) : (
              <Button
                color="secondary"
                disabled={processingAction}
                onClick={() => handleTriggerAction("SET_OFFLINE")}
                startIcon={
                  actionTriggered === "SET_OFFLINE" && processingAction ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Set Offline
              </Button>
            )}

            <Divider orientation="vertical" flexItem />
          </>
        )}
        {mode != null && (
          <>
            <Typography>Mode: {mode}</Typography>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        {sync != null && (
          <>
            <Typography>Sync: {sync ? "Yes" : "No"}</Typography>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        {lb != null && (
          <>
            <Typography>Load balancer: {lb}</Typography>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        {notifications != null && (
          <>
            <Typography>Notifications: {notifications}</Typography>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        {update != null && (
          <>
            <Typography>Update: {update ? "Yes" : "No"}</Typography>
            <Divider orientation="vertical" flexItem />
          </>
        )}

        <Typography>Process status: {process_status}</Typography>
        <Divider orientation="vertical" flexItem />
        <Typography>
          Timestamp: {new Date(timestamp).toLocaleString()}{" "}
        </Typography>

        {!logMasterHandler && (
          <>
            {showDetails ? (
              <IconButton
                onClick={() => {
                  if (notifications == null) {
                    setShowDetails((prev) => !prev);
                  }
                }}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => {
                  if (notifications == null) {
                    setShowDetails((prev) => !prev);
                  }
                }}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            )}
          </>
        )}

        {logMasterHandler && (
          <>
            <Divider orientation="vertical" flexItem />
            <Button
              color="secondary"
              disabled={fetchingLogMaster}
              onClick={getLogMaster}
              startIcon={
                fetchingLogMaster ? <CircularProgress size={16} /> : null
              }
            >
              {logMaster != null && showLogMaster ? "Reload Logs" : "Log"}
            </Button>
          </>
        )}
      </Box>

      {showDetails && (
        <>
          <Divider sx={{ marginY: 1 }} />
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box display={"flex"} alignItems={"center"} gap={1}>
              <Typography>Orchestration actions:</Typography>
              <Button
                color="secondary"
                disabled={processingAction}
                onClick={() => handleTriggerAction("LOCAL_MODE")}
                startIcon={
                  actionTriggered === "LOCAL_MODE" && processingAction ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Local mode
              </Button>
              <Button
                color="secondary"
                disabled={processingAction}
                onClick={() => handleTriggerAction("ORCHESTRATION_MODE")}
                startIcon={
                  actionTriggered === "ORCHESTRATION_MODE" &&
                  processingAction ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Orchestration mode
              </Button>
            </Box>
            <Divider />
            <Box display={"flex"} alignItems={"center"} gap={1}>
              <Typography>Process actions:</Typography>
              <Button
                color="secondary"
                disabled={processingAction}
                onClick={() => handleTriggerAction("FORCE_UPDATE")}
                startIcon={
                  actionTriggered === "FORCE_UPDATE" && processingAction ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Force update
              </Button>
              <Button
                color="secondary"
                disabled={processingAction}
                onClick={() => handleTriggerAction("PREVENT_UPDATE")}
                startIcon={
                  actionTriggered === "PREVENT_UPDATE" && processingAction ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Prevent update
              </Button>
              <Button
                color="secondary"
                disabled={processingAction}
                onClick={() => handleTriggerAction("STANDARD_UPDATE")}
                startIcon={
                  actionTriggered === "STANDARD_UPDATE" && processingAction ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Standard update
              </Button>
            </Box>
          </Box>
        </>
      )}

      {showLogMaster && (
        <>
          <Divider sx={{ marginY: 1 }} />
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              // p={1}
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <Table size="small">
                <TableHead
                  sx={{
                    backgroundColor: "#e0e0e0",
                    position: "sticky",
                    top: 0,
                  }}
                >
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Elapsed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logMaster?.table && logMaster.table.length > 0 ? (
                    logMaster.table.map((row) => (
                      <TableRow
                        key={row?.name}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>
                          <strong>{row?.name}</strong>
                        </TableCell>
                        <TableCell>{row?.started}</TableCell>
                        <TableCell>{row?.elapsed}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No logs available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
          <Box pt={1} display={"flex"} justifyContent={"flex-end"}>
            <Button onClick={() => setShowLogMaster(false)}>Close</Button>
          </Box>
        </>
      )}
    </Box>
  );
}
